import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import SectionHeader from '../../components/admin/SectionHeader';
import StatCard from '../../components/admin/StatCard';
import DataTable from '../../components/admin/DataTable';
import Badge from '../../components/admin/Badge';

const REASON_COLORS = {
  too_expensive:    '#ef4444',
  not_using:        '#f97316',
  switching:        '#f59e0b',
  missing_features: '#8b5cf6',
  technical_issues: '#3b82f6',
  temporary_pause:  '#14b8a6',
  other:            '#6b7280',
  none:             '#4b5563',
};

const ALL_REASONS = [
  { value: 'too_expensive',    label: 'Too expensive' },
  { value: 'not_using',        label: 'Not using it enough' },
  { value: 'switching',        label: 'Switching to another service' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'technical_issues', label: 'Technical issues' },
  { value: 'temporary_pause',  label: 'Pausing temporarily' },
  { value: 'other',            label: 'Other' },
];

// Human-readable labels for structured follow-up fields
const FOLLOWUP_LABELS = {
  budget:            'Willing to pay',
  competitor:        'Switching to',
  competitor_reason: 'Why they switched',
  features:          'Missing features',
  issue:             'Issues experienced',
  improvement:       'Would have stayed if',
  return_when:       'Plans to return',
  details:           'Details',
};

// Parse cancellation_details — may be JSON (new) or plain text (legacy)
function parseDetails(str) {
  if (!str) return null;
  try {
    const parsed = JSON.parse(str);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) return parsed;
  } catch {}
  return { details: str }; // legacy plain text
}

function DetailsPanel({ entry }) {
  const parsed = parseDetails(entry.details);
  if (!parsed) return null;
  const entries = Object.entries(parsed);
  return (
    <div style={{
      marginTop: '12px',
      padding: '14px 16px',
      background: 'var(--admin-elevated)',
      borderRadius: '8px',
      borderLeft: `3px solid ${REASON_COLORS[entry.reason] || '#888'}`,
    }}>
      <p style={{ fontSize: '12px', color: 'var(--admin-text-muted)', margin: '0 0 10px 0' }}>
        <strong style={{ color: 'var(--admin-text-primary)' }}>{entry.email}</strong> — {entry.reasonLabel}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {entries.map(([key, val]) => (
          <div key={key}>
            <span style={{ fontSize: '11px', color: 'var(--admin-text-muted)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {FOLLOWUP_LABELS[key] || key}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--admin-text-primary)', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
              {val}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', padding: '10px 14px', fontSize: '12px' }}>
      <p style={{ color: '#aaa', margin: '0 0 6px 0', fontWeight: 600 }}>{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color, margin: '2px 0' }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
}

function AdminCancellations({ apiFetch, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reasonFilter, setReasonFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(null); // subscriptionId

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/admin/cancellation-insights');
      if (!res.ok) throw new Error('Failed');
      setData(await res.json());
    } catch {
      toast.error('Error', 'Failed to load cancellation data');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, toast]);

  useEffect(() => { load(); }, [load]);

  const filteredEntries = useMemo(() => {
    if (!data?.entries) return [];
    return data.entries.filter(e => {
      const effectiveReason = e.reason || 'none';
      if (reasonFilter !== 'all' && effectiveReason !== reasonFilter) return false;
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        // Search across email, plan, domain, and all details values
        const detailsText = (() => {
          const parsed = parseDetails(e.details);
          if (!parsed) return '';
          return Object.values(parsed).join(' ').toLowerCase();
        })();
        if (!e.email.toLowerCase().includes(q) &&
            !e.planSlug.toLowerCase().includes(q) &&
            !e.domain.toLowerCase().includes(q) &&
            !detailsText.includes(q)) return false;
      }
      return true;
    });
  }, [data, reasonFilter, statusFilter, search]);

  const entryColumns = [
    { key: 'date', header: 'Date', width: '110px', render: r => {
      const d = r.requestedAt || r.cancelledAt;
      return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    }},
    { key: 'email', header: 'Customer', render: r => r.email || '—' },
    { key: 'plan', header: 'Plan', render: r => r.planSlug ? <Badge variant="info">{r.planSlug}</Badge> : '—' },
    { key: 'domain', header: 'Domain', cellStyle: { color: 'var(--admin-text-secondary)', fontSize: '12px' }, render: r => r.domain || '—' },
    { key: 'reason', header: 'Reason', render: r => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: REASON_COLORS[r.reason || 'none'] || '#888', flexShrink: 0 }} />
        <span style={{ fontSize: '13px' }}>{r.reasonLabel}</span>
      </span>
    )},
    { key: 'status', header: 'Status', width: '100px', render: r => (
      <Badge variant={r.status === 'pending' ? 'warning' : 'error'}>
        {r.status === 'pending' ? 'Pending' : 'Cancelled'}
      </Badge>
    )},
    { key: 'details', header: 'Details', render: r => {
      const parsed = parseDetails(r.details);
      if (!parsed) return <span style={{ color: 'var(--admin-text-muted)', fontSize: '12px' }}>—</span>;
      // Show a brief summary of the first field
      const firstKey = Object.keys(parsed)[0];
      const firstVal = parsed[firstKey] || '';
      const preview = firstKey ? `${FOLLOWUP_LABELS[firstKey] || firstKey}: ${firstVal.length > 40 ? firstVal.slice(0, 40) + '…' : firstVal}` : '';
      const isOpen = detailsOpen === r.subscriptionId;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          {preview && !isOpen && (
            <span style={{ fontSize: '11px', color: 'var(--admin-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
              {preview}
            </span>
          )}
          <button
            className="admin-btn admin-btn--sm admin-btn--ghost"
            onClick={() => setDetailsOpen(isOpen ? null : r.subscriptionId)}
            style={{ fontSize: '11px', flexShrink: 0 }}
          >
            {isOpen ? 'Hide' : 'View'}
          </button>
        </div>
      );
    }},
  ];

  if (loading && !data) {
    return (
      <div>
        <SectionHeader title="Cancellation Insights" description="Loading data..." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {[1,2,3].map(i => <div key={i} className="admin-card" style={{ height: '90px' }}><div className="admin-skeleton" style={{ width: '60%', height: '14px', marginBottom: '8px' }} /><div className="admin-skeleton" style={{ width: '35%', height: '26px' }} /></div>)}
        </div>
      </div>
    );
  }

  const breakdown = data?.breakdown || [];
  const trend = data?.trend || [];
  const total = data?.total || 0;
  const pendingCount = data?.entries?.filter(e => e.status === 'pending').length || 0;
  const topReason = breakdown[0];

  return (
    <div>
      <SectionHeader
        title="Cancellation Insights"
        description="Analyse why customers cancel to identify areas for improvement"
        actions={
          <button className="admin-btn admin-btn--secondary" onClick={load} disabled={loading}>
            <i className="fa-solid fa-rotate-right" style={{ marginRight: 6 }} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        }
      />

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <StatCard label="Total Cancellations" value={total} icon="fa-solid fa-ban" color="var(--admin-error)" />
        <StatCard label="With Reason" value={data?.entries?.filter(e => e.reason).length || 0} icon="fa-solid fa-comment" color="var(--admin-text-secondary)" />
        <StatCard label="Pending Cancellations" value={pendingCount} icon="fa-solid fa-clock" color="var(--admin-warning)" />
        <StatCard
          label="Top Reason"
          value={topReason ? `${topReason.percentage}%` : '—'}
          icon="fa-solid fa-circle-exclamation"
          color={topReason ? REASON_COLORS[topReason.reason] : 'var(--admin-text-muted)'}
          sub={topReason?.label || ''}
        />
      </div>

      {(data?.entries?.length || 0) === 0 ? (
        <div className="admin-card" style={{ textAlign: 'center', padding: '48px' }}>
          <i className="fa-solid fa-circle-check" style={{ fontSize: '40px', color: 'var(--admin-success)', marginBottom: '16px' }} />
          <p style={{ color: 'var(--admin-text-secondary)', fontSize: '14px' }}>No cancellations with recorded reasons yet.</p>
        </div>
      ) : (
        <>
          {/* Charts row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
            {/* Pie / donut breakdown */}
            <div className="admin-card">
              <h3 className="admin-card-title" style={{ marginBottom: '16px' }}>Reason Breakdown</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={breakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="count"
                    nameKey="label"
                    paddingAngle={2}
                  >
                    {breakdown.map(entry => (
                      <Cell key={entry.reason} fill={REASON_COLORS[entry.reason] || '#888'} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value} (${breakdown.find(b => b.label === name)?.percentage ?? 0}%)`, name]}
                    contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', fontSize: '12px' }}
                  />
                  <Legend
                    formatter={(value, entry) => (
                      <span style={{ color: 'var(--admin-text-primary)', fontSize: '11px' }}>
                        {value} <span style={{ color: 'var(--admin-text-muted)' }}>({entry.payload.count})</span>
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Bar chart — horizontal breakdown */}
            <div className="admin-card">
              <h3 className="admin-card-title" style={{ marginBottom: '16px' }}>Reason Count</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={breakdown} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#888', fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" width={150} tick={{ fill: '#aaa', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {breakdown.map(entry => (
                      <Cell key={entry.reason} fill={REASON_COLORS[entry.reason] || '#888'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Monthly trend */}
          {trend.some(t => t.total > 0) && (
            <div className="admin-card" style={{ marginBottom: '24px' }}>
              <h3 className="admin-card-title" style={{ marginBottom: '16px' }}>Monthly Cancellation Trend</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trend} margin={{ left: 0, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#888', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  {ALL_REASONS.map(r => (
                    <Bar key={r.value} dataKey={r.value} name={r.label} stackId="a" fill={REASON_COLORS[r.value]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Detail table */}
          <div className="admin-card">
            <div className="admin-flex-between" style={{ marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
              <h3 className="admin-card-title">Individual Cancellations</h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  className="admin-input"
                  placeholder="Search email, plan, domain, details…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ fontSize: '13px', padding: '6px 10px', width: '220px' }}
                />
                <select className="admin-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: '13px' }}>
                  <option value="all">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select className="admin-select" value={reasonFilter} onChange={e => setReasonFilter(e.target.value)} style={{ fontSize: '13px' }}>
                  <option value="all">All reasons</option>
                  <option value="none">No reason provided</option>
                  {ALL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>

            <p style={{ fontSize: '12px', color: 'var(--admin-text-muted)', marginBottom: '12px' }}>
              {filteredEntries.length} of {total} record{total !== 1 ? 's' : ''}
            </p>

            <DataTable columns={entryColumns} data={filteredEntries} />

            {/* Inline expanded details panel */}
            {detailsOpen && (() => {
              const e = filteredEntries.find(x => x.subscriptionId === detailsOpen) ||
                        data.entries.find(x => x.subscriptionId === detailsOpen);
              if (!e) return null;
              return (
                <div ref={el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }}>
                  <DetailsPanel entry={e} />
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminCancellations;
